import { Injectable, inject, signal, effect } from '@angular/core';
import { Router, NavigationEnd, NavigationStart, ActivatedRoute } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ToastService } from './toast.service';

export interface NavEntry {
    url: string;
    state?: any;
}

@Injectable({ providedIn: 'root' })
export class NavigationService {
    public readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly toastService = inject(ToastService);
    private readonly STORAGE_KEY = 'app_nav_history';

    // Inizializziamo il segnale leggendo dal sessionStorage (se esiste)
    private readonly history = signal<NavEntry[]>(this.loadFromStorage());
    restoredState = signal<any>(null);

    constructor() {
        // Ogni volta che il segnale history cambia, lo salviamo automaticamente
        effect(() => {
            sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.history()));
        });

        this.router.events.pipe(
            filter(event => event instanceof NavigationStart)
        ).subscribe((event: any) => {
            if (event.navigationTrigger === 'popstate') {
                const currentUrl = event.url.split('?')[0].replace(/\/$/, ""); // Rimuove query params e slash finale
                const savedEntry = this.history().find(h =>
                    h.url.split('?')[0].replace(/\/$/, "") === currentUrl
                );

                this.restoredState.set(savedEntry?.state || null);
            } else {
                this.restoredState.set(null);
            }
        });

        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe((event: any) => {
            //Quando cambio pagina pulisco la lista dei toast
            this.toastService.clear();
            this.pushHistory(event.urlAfterRedirects);
        });
    }

    private loadFromStorage(): NavEntry[] {
        const stored = sessionStorage.getItem(this.STORAGE_KEY);
        return stored ? JSON.parse(stored) : [];
    }

    private pushHistory(url: string) {
        this.history.update(prev => {
            // Estraiamo i query params dall'URL corrente
            const urlTree = this.router.parseUrl(url);
            const queryParams = urlTree.queryParams;

            if (prev.length > 0 && prev[prev.length - 1].url === url) return prev;

            return [...prev, {
                url,
                state: { queryParams }
            }].slice(-10);
        });
    }

    setCurrentState(data: any) {
        this.history.update(prev => {
            if (prev.length === 0) return prev;
            const updated = [...prev];
            updated[updated.length - 1] = { ...updated[updated.length - 1], state: data };
            return updated;
        });
    }

    clearQueryParamsSilently() {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {},
            queryParamsHandling: null,
            replaceUrl: true // Rimpiazza lo stato corrente della cronologia
        });
    }

    getQueryParams() {
        const urlTree = this.router.parseUrl(this.router.url);
        return urlTree.queryParams;
    }

    getQueryParam(key: string): string | null {
        const params = this.getQueryParams();
        return params[key] || null;
    }


    getEffectiveState() {
        // 1. Recuperiamo lo stato dai due possibili "contenitori" di memoria
        const navState = this.router.currentNavigation()?.extras.state;
        const restState = this.restoredState();

        // Uniamo gli stati di memoria (navState ha la precedenza su restState)
        const memoryState = { ...(restState || {}), ...(navState || {}) };

        // 2. Recuperiamo i Query Params reali dall'URL corrente
        const urlParams = this.getQueryParams();

        // Se non c'è nulla in memoria e nulla nell'URL, restituiamo null
        if (Object.keys(memoryState).length === 0 && Object.keys(urlParams).length === 0) {
            return null;
        }

        // 3. COSTRUZIONE DELLO STATO FINALE CON PRIORITÀ URL
        const finalState = { ...memoryState };

        // Assicuriamoci che esista l'oggetto filter
        finalState.filter = { ...(finalState.filter || {}) };

        // Sovrascriviamo i filtri con i parametri dell'URL
        for (const key in urlParams) {
            if (Object.hasOwn(urlParams, key)) {
                // Gestione speciale per il sort (deve essere sempre un array)
                if (key === 'sort') {
                    finalState.filter[key] = Array.isArray(urlParams[key])
                        ? urlParams[key]
                        : [urlParams[key]];
                } else {
                    finalState.filter[key] = urlParams[key];
                }
            }
        }

        // Se l'URL ha dei parametri, aggiorniamo anche la ricerca corrente (currentSearch)
        if (urlParams['query']) {
            finalState.currentSearch = urlParams['query'];
        }

        return finalState;
    }

    formatSortFromParams(sortParam: any): string[] {
        if (!sortParam) return [];
        return Array.isArray(sortParam) ? sortParam : [sortParam];
    }

    goBack(defaultPath: string = '/'): void {
        const target = this.getLastAndRemove();
        if (target != null) {
            this.router.navigateByUrl(target.url, { state: target.state });
        } else {
            this.router.navigateByUrl(defaultPath);
        }
    }

    goTopath(navigete: any[], stateObj: any) {
        this.setCurrentState(stateObj);
        this.goTo(navigete);
    }

    goTo(navigete: any[]) {
        this.router.navigate(navigete);
    }

    goToWithParams(navigete: any[], extras: any) {
        this.router.navigate(navigete, extras);
    }

    getLastAndRemove() {
        const stack = this.history();
        let target = null;
        if (stack.length > 1) {
            const newStack = [...stack];
            newStack.pop();
            target = newStack[newStack.length - 1];
            this.history.set(newStack);
        }
        return target;
    }

    goToEdit(navigete: any[]) {
        this.getLastAndRemove();
        this.goTo(navigete);
    }







}